import React, { useState, Fragment } from "react";

import { Menu, Transition } from "@headlessui/react";
import { ReactSVG } from "react-svg";

import { calculateAge, formatToYYYY_MMM_DD } from "../../../../helpers/functions";
import PersonalInformationCreate from "./PersonalInformationCreate";
import {
  biologicalSexOptions,
  characteristicsOptions,
  genderOptions,
  medicationAdministrationOptions,
  preferencesOptions,
  raceOptions,
} from "../../create/options";

const PersonalInformation = ({ patient, conditions = [] }) => {

  const getStatusReason = () => {
    const statusToUse = patient?.status;
    if (patient?.status && patient?.status !== "active") {
      return statusToUse;
    } else {
      return "";
    }
  };
  const getStatus = () => {
    if (patient?.status && patient.status === "active") {
      return "active";
    } else {
      return "inactive";
    }
  };

  const getPreferences = (pref) => {
    if (!pref) return "None";
    return pref
      .split(",")
      .map((val) => preferencesOptions?.find((option) => option.value === val.trim())?.label ?? val.trim())
      .join(', ');
  }

  const [showCreateSlider, setShowCreateSlider] = useState(false);
  const [openStatus, setOpenStatus] = useState(false);

  return (
    <div className="mainCard !w-[550px] !h-fit !min-h-[613px]">
      {(
        <PersonalInformationCreate
          open={showCreateSlider}
          onClose={(value) => {
            setShowCreateSlider(value);
          }}
          patient={patient}
          conditions={conditions}
        />
      )}
      <div className="flex items-center justify-between w-full h-[72px] px-6 py-3 border-b border-gray-100 bg-gray-20">
        <h4 className="text-lg font-bold text-teal-600">Personal Information</h4>
        <Menu as="div" className="relative inline-block text-left">
          <div>
            <Menu.Button>
              <ReactSVG
                src={`/icons/triple-dot.svg`}
                className={`flex text-black !items-center w-6 scale-x-[1.4] scale-y-[1.4]`}
              />
            </Menu.Button>
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform translate-y-2 opacity-0 scale-95"
            enterTo="transform translate-y-0 opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform translate-y-0 opacity-100 scale-100"
            leaveTo="transform translate-y-2 opacity-0 scale-95"
            >
            <Menu.Items className="absolute top-full left-1/2 -translate-x-1/2 mt-1 min-w-[80px] w-auto origin-top bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
              <Menu.Item>
                <button
                  className="block text-left w-[80px] h-[38px] px-[10px] py-[10px] flex items-center gap-[10px] border-b border-b-neutral-70 hover:bg-gray-50"
                  onClick={() => {
                    setShowCreateSlider(true);
                  }}
                >
                  Edit
                </button>
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
      <div className="px-6 py-3.5 w-full">
        <div className="grid grid-cols-2 ">
          <div>
            <p className="mb-1.5 text-sm text-gray-700">Last Name</p>
            <p className="font-bold text-gray-800 capitalize text-body-md">
              {patient.lastName}
            </p>
          </div>
          <div>
            <p className="mb-1.5 text-sm text-gray-700">Status</p>
            <p
              onClick={() => setOpenStatus(true)}
              className="font-bold text-green-600 capitalize text-body-md"
            >
              {getStatus()}
            </p>
          </div>
          <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

          <div>
            <p className="mb-1.5 text-sm text-gray-700">First Name</p>
            <p className="font-bold text-gray-800 capitalize text-body-md">
              {patient?.firstName}
            </p>
          </div>
          <div>
            <p className="mb-1.5 text-sm text-gray-700">Middle Name</p>
            <p className="font-bold text-gray-800 capitalize text-body-md">
              {patient?.middleName}
            </p>
          </div>
          <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

          <div>
            <p className="mb-1.5 text-sm text-gray-700">Date of Birth</p>
            <p className="font-bold text-gray-800 text-body-md">
              {formatToYYYY_MMM_DD(patient.dateOfBirth)}
            </p>
          </div>
          <div>
            <p className="mb-1.5 text-sm text-gray-700">Age</p>
            <p className="font-bold text-gray-800 text-body-md">
              {calculateAge(patient.dateOfBirth)}
            </p>
          </div>
          <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>
          <div>
            <p className="mb-1.5 text-sm text-gray-700">Biological sex</p>
            <p className="font-bold text-gray-800 capitalize text-body-md">
              {biologicalSexOptions.find(
                (g) => g.value === patient.biologicalSex
              )?.label || "Prefer Not to Respond"}
            </p>
          </div>
          <div>
            <p className="mb-1.5 text-sm text-gray-700">Race</p>
            <p className="font-bold text-gray-800 capitalize text-body-md">
              {raceOptions.find((g) => g.value === patient.race)?.label}
            </p>
          </div>
          <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>
          <div>
            <p className="mb-1.5 text-sm text-gray-700">Gender</p>
            <p className="font-bold text-gray-800 capitalize text-body-md">
              {genderOptions.find((g) => g.value === patient.gender)?.label ||
                "Prefer Not to Respond"}
            </p>
          </div>
          <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>
          <div className="col-span-1">
            <p className="mb-1.5 text-sm text-gray-700">Primary Language</p>
            <p className="font-bold text-gray-800 capitalize text-body-md">
              {patient.primaryLanguage || "None"}
            </p>
          </div>
          <div className="col-span-1">
            <p className="mb-1.5 text-sm text-gray-700">Preferences</p>
            <p className="font-bold text-gray-800 text-body-md">
              {getPreferences(patient.preferences)}
            </p>
          </div>
          <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>
          <div>
            <p className="mb-1.5 text-sm text-gray-700">Medication Administration</p>
            <p className="font-bold text-gray-800 capitalize text-body-md">
                {medicationAdministrationOptions.find((g) => g.value === patient.medicationAdministration)?.label}
            </p>
          </div>
          <div>
            <p className="mb-1.5 text-sm text-gray-700">Characteristics</p>
            <p className="font-bold text-gray-800 normal-case text-body-md">
              {patient.characteristics?.split(",").filter((val) => val !== null && val.trim().length > 0)
              .map((val) => characteristicsOptions?.find((option) => option.value === val.trim())?.header)
              .join(", ")}
            </p>
          </div>

          {/* <div>
            <p className="mb-1.5 text-sm text-gray-700">Ethnicity</p>
            <p className="font-bold text-black text-body-md">
              {patient.ethnicity || "Caucasian"}
            </p>
          </div> */}

          {getStatusReason() && (
            <div>
              <p className="mb-1.5 text-sm text-gray-700">Status Reason</p>
              <p
                className="font-bold text-green-600 capitalize text-body-md"
              >
                {getStatusReason()}
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default PersonalInformation;
